import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ActivityProgressService {

  private parseToLocalMs(startTime: string): number {
    return new Date(startTime).getTime();
  }

  getElapsedPercent(startTime: string, expectedDurationMs?: number, now: number = Date.now()): number {
    if (!startTime || !expectedDurationMs) return 0;
    const startMs = new Date(startTime).getTime();
    const elapsedMs = now - startMs;
    if (elapsedMs < 0) return 0;
    const percent = Math.round((elapsedMs / expectedDurationMs) * 100);
    return Math.min(percent, 100);
  }

  getTimeRemaining(startTime: string, expectedDurationMs?: number, now: number = Date.now()): string {
    if (!startTime || !expectedDurationMs) return '';

    const startMs = this.parseToLocalMs(startTime);
    if (startMs === 0) return '';

    const elapsedMs = now - startMs;

    // if start is in future, elapsedMs is negative → remaining would be wrong
    if (elapsedMs < 0) return '';

    const remainingMs = expectedDurationMs - elapsedMs;
    if (remainingMs <= 0) return '';

    const totalSeconds = Math.floor(remainingMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const secs = totalSeconds % 60;
    const mins = totalMinutes % 60;
    const hrs = totalHours % 24;

    if (totalDays >= 1) {
      const parts = [`${totalDays}d`];
      if (hrs > 0) parts.push(`${hrs}h`);
      if (mins > 0) parts.push(`${mins}m`);
      if (secs > 0) parts.push(`${secs}s`);
      return parts.join(' ') + ' left';
    }
    if (totalHours >= 1) {
      const parts = [`${totalHours}h`];
      if (mins > 0) parts.push(`${mins}m`);
      if (secs > 0) parts.push(`${secs}s`);
      return parts.join(' ') + ' left';
    }
    if (totalMinutes > 0) {
      return secs > 0 ? `${totalMinutes}m ${secs}s left` : `${totalMinutes}m left`;
    }
    return `${secs}s left`;
  }

  //  check if time is done
  isCompleted(startTime: string, expectedDurationMs?: number, now: number = Date.now()): boolean {
    if (!startTime || !expectedDurationMs) return false;
    return (now - this.parseToLocalMs(startTime)) >= expectedDurationMs;
  }
  
  /**
   * 
   * @param startTime 
   * @param expectedDurationMs 
   * @param now 
   * @returns true if threshold reached
   */
  isThresholdReached(startTime: string,expectedDurationMs?: number, now: number = Date.now()): boolean {
    if (!startTime || !expectedDurationMs) return false;
    const startMs = new Date(startTime).getTime();
    const elapsedMs = now - startMs;
    return elapsedMs >= expectedDurationMs;
  }

  /** Formats raw ms → "2mins", "1h 5m 30s" */
  formatDuration(ms: number): string {
    if (!ms || ms <= 0) return '—';
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hrs = Math.floor((totalSeconds % 86400) / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (days >= 1) {
      const parts = [`${days}d`];
      if (hrs > 0) parts.push(`${hrs}h`);
      if (mins > 0) parts.push(`${mins}m`);
      if (secs > 0) parts.push(`${secs}s`);
      return parts.join(' ');
    }
    if (hrs >= 1) {
      const parts = [`${hrs}h`];
      if (mins > 0) parts.push(`${mins}m`);
      if (secs > 0) parts.push(`${secs}s`);
      return parts.join(' ');
    }
    if (mins > 0) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    return `${secs}s`;
  }

  /** Live elapsed time — pass currentTime from component's 1s interval */
  getElapsedTime(startTime: string, now: number = Date.now()): string {
    if (!startTime) return '-';
    const elapsedMs = now - new Date(startTime).getTime();
    if (elapsedMs < 0) return '0s';
    return this.formatDuration(elapsedMs);
  }

  // format remaining time to HH:MM:SS
  formatTimeRemaining(startTime: string, expectedDurationMs?: number, now: number = Date.now()): string {
    if (!startTime || !expectedDurationMs) return '';
    const startMs = this.parseToLocalMs(startTime);
    if (startMs === 0) return '';
    const elapsedMs = now - startMs;
    if (elapsedMs < 0) return '';
    const remainingMs = expectedDurationMs - elapsedMs;
    if (remainingMs <= 0) return '';
    const totalSeconds = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  // format elapsed time to HH:MM:SS
  formatElapsedTime(startTime: string, now: number = Date.now()): string {
    if (!startTime) return '';
    const startMs = this.parseToLocalMs(startTime);
    if (startMs === 0) return '';
    const elapsedMs = now - startMs;
    if (elapsedMs < 0) return '';
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  getEnvironmentStatus ( operationType: string | undefined, status: 'inprogress' | 'failed'): any {
    const OP_TOOLTIP_KEYS: Partial<Record<string, { inprogress: string; failed: string }>> = {
      sanity_test:              { inprogress: 'BLUEPRINT_SUMMARY.SANITY_TEST_INPROGRESS',              failed: 'BLUEPRINT_SUMMARY.SANITY_TEST_FAILED' },
      ef_test:                  { inprogress: 'BLUEPRINT_SUMMARY.EF_TEST_INPROGRESS',                  failed: 'BLUEPRINT_SUMMARY.EF_TEST_FAILED' },
      config_rvt_test:          { inprogress: 'BLUEPRINT_SUMMARY.CONFIG_RVT_TEST_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.CONFIG_RVT_TEST_FAILED' },
      rvt_test:                 { inprogress: 'BLUEPRINT_SUMMARY.RVT_TEST_INPROGRESS',                 failed: 'BLUEPRINT_SUMMARY.RVT_TEST_FAILED' },
      inventory:                { inprogress: 'BLUEPRINT_SUMMARY.INVENTORY_INPROGRESS',                failed: 'BLUEPRINT_SUMMARY.INVENTORY_FAILED' },
      backup_create:            { inprogress: 'BLUEPRINT_SUMMARY.BACKUP_CREATE_INPROGRESS',            failed: 'BLUEPRINT_SUMMARY.BACKUP_CREATE_FAILED' },
      backup_delete:            { inprogress: 'BLUEPRINT_SUMMARY.BACKUP_DELETE_INPROGRESS',            failed: 'BLUEPRINT_SUMMARY.BACKUP_DELETE_FAILED' },
      backup_update:            { inprogress: 'BLUEPRINT_SUMMARY.BACKUP_UPDATE_INPROGRESS',            failed: 'BLUEPRINT_SUMMARY.BACKUP_UPDATE_FAILED' },
      cob_config:               { inprogress: 'BLUEPRINT_SUMMARY.COB_CONFIG_INPROGRESS',               failed: 'BLUEPRINT_SUMMARY.COB_CONFIG_FAILED' },
      cob_reconfig:             { inprogress: 'BLUEPRINT_SUMMARY.COB_RECONFIG_INPROGRESS',             failed: 'BLUEPRINT_SUMMARY.COB_RECONFIG_FAILED' },
      cob_update:               { inprogress: 'BLUEPRINT_SUMMARY.COB_UPDATE_INPROGRESS',               failed: 'BLUEPRINT_SUMMARY.COB_UPDATE_FAILED' },
      cob_delete:               { inprogress: 'BLUEPRINT_SUMMARY.COB_DELETE_INPROGRESS',               failed: 'BLUEPRINT_SUMMARY.COB_DELETE_FAILED' },
      runbook_provision:        { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_PROVISION_INPROGRESS',        failed: 'BLUEPRINT_SUMMARY.RUNBOOK_PROVISION_FAILED' },
      runbook_redeploy:         { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_REDEPLOY_INPROGRESS',         failed: 'BLUEPRINT_SUMMARY.RUNBOOK_REDEPLOY_FAILED' },
      runbook_config:           { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_CONFIG_INPROGRESS',           failed: 'BLUEPRINT_SUMMARY.RUNBOOK_CONFIG_FAILED' },
      runbook_update:           { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_UPDATE_INPROGRESS',           failed: 'BLUEPRINT_SUMMARY.RUNBOOK_UPDATE_FAILED' },
      runbook_delete:           { inprogress: 'BLUEPRINT_SUMMARY.RUNBOOK_DELETE_INPROGRESS',           failed: 'BLUEPRINT_SUMMARY.RUNBOOK_DELETE_FAILED' },
      schedule_create:          { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_CREATE_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.SCHEDULE_CREATE_FAILED' },
      schedule_config_update:   { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_CONFIG_UPDATE_INPROGRESS',   failed: 'BLUEPRINT_SUMMARY.SCHEDULE_CONFIG_UPDATE_FAILED' },
      schedule_enable:          { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_ENABLE_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.SCHEDULE_ENABLE_FAILED' },
      schedule_disable:         { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_DISABLE_INPROGRESS',         failed: 'BLUEPRINT_SUMMARY.SCHEDULE_DISABLE_FAILED' },
      schedule_delete:          { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULE_DELETE_INPROGRESS',          failed: 'BLUEPRINT_SUMMARY.SCHEDULE_DELETE_FAILED' },
      scheduler_version_update: { inprogress: 'BLUEPRINT_SUMMARY.SCHEDULER_VERSION_UPDATE_INPROGRESS', failed: 'BLUEPRINT_SUMMARY.SCHEDULER_VERSION_UPDATE_FAILED' },
    };
    if (!operationType) { return null;}
    return OP_TOOLTIP_KEYS[operationType]?.[status] ?? null;
  }

  updateInstanceActivityDetail(data: any, detail: any) {
    return data.map(item => {
      if (item.id !== detail.instanceId) { return item; }
      const stages = detail?.stages ?? [];
      const execStage = stages.find((stage: any) => stage.type === 'component_execution');
      const countData: any = {};
      if (execStage?.count?.total) {
        const total = Number(execStage.count.total);
        const completed = Number(execStage.count.completed ?? 0);
        const skipped = Number(execStage.count.skipped ?? 0);
        countData.total = total;
        countData.completedPercent = completed !== 0 ? Math.round(((completed + skipped) / total) * 100) : 0;
      }
      return { ...item, activityDetails: {...detail,countData}, activityStartTime: detail?.startTime, expectedDurationMs: detail?.expectedDurationMs};
    });
  }
  // Returns remaining time until expected completion in "XdYhZmZs" format (shows days if > 24 hours)
  timeDifference(startTime: string,expectedDurationMs?: number,now: number = Date.now()): string {
    if (!startTime || !expectedDurationMs) return '';
    const startMs = this.parseToLocalMs(startTime);
    if (startMs === 0) return '';
    const elapsedMs = now - startMs;
    if (elapsedMs < 0) return '';
    const remainingMs = expectedDurationMs - elapsedMs;
    if (remainingMs <= 0) return '0s';
    return this.formatDuration(remainingMs);
  }

  /**
   * Calculates the forceAbort flag for instance abort API
   * @param activityData - The activity details object containing stages array
   * @returns boolean - forceAbort value to pass to API
   */
  calculateForceAbort(activityData: any): boolean {
    const stages: any[] = activityData?.stages ?? [];
    const execStage = stages.find((s: any) => s.type === 'component_execution' && s.status === 'inprogress');
    const components: any[] = execStage?.components ?? [];
    return !execStage || components.length === 0;
  }
}